import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export enum ConnectionFileStatus {
  UPLOADED = 'UPLOADED',
  NOT_UPLOADED = 'NOT_UPLOADED',
  ARCHIVED = 'ARCHIVED',
  ERRORED = 'ERRORED',
}

export const FILE_STATUS_TO_TEXT: {[status in ConnectionFileStatus]: string} = {
  [ConnectionFileStatus.UPLOADED]: 'Available',
  [ConnectionFileStatus.ARCHIVED]: 'Archived',
  [ConnectionFileStatus.NOT_UPLOADED]: 'Pending',
  [ConnectionFileStatus.ERRORED]: 'Unavailable',
};

export const FILE_STATUS_TO_ICON: {[status in ConnectionFileStatus]: BenchlingIconName} = {
  [ConnectionFileStatus.UPLOADED]: 'request-executed',
  [ConnectionFileStatus.ARCHIVED]: 'archive',
  [ConnectionFileStatus.NOT_UPLOADED]: 'request-in-progress',
  [ConnectionFileStatus.ERRORED]: 'warning',
};

export const FILE_STATUS_TO_STYLE: {[status in ConnectionFileStatus]: keyof typeof styles} = {
  [ConnectionFileStatus.UPLOADED]: 'uploaded',
  [ConnectionFileStatus.ARCHIVED]: 'archived',
  [ConnectionFileStatus.NOT_UPLOADED]: 'not_uploaded',
  [ConnectionFileStatus.ERRORED]: 'errored',
};

const styles = StyleSheet.create({
  uploaded: {
    color: Colors.GREEN,
  },
  not_uploaded: {
    color: Colors.GRAY_DARK,
  },
  archived: {
    color: Colors.GRAY,
  },
  errored: {
    color: Colors.RED,
  },
  statusIcon: {
    marginRight: '6px',
  },
});

// These statuses come from PipelineDataUploadStatus as defined data_frame.graphql,
// but we may not use every status there in the UI.
export const MAP_FILE_UPLOAD_STATUS_TO_CONNECTION_FILE_STATUS: Record<string, ConnectionFileStatus> = {
  NOT_UPLOADED: ConnectionFileStatus.NOT_UPLOADED,
  SUCCEEDED: ConnectionFileStatus.UPLOADED,
  IN_PROGRESS: ConnectionFileStatus.NOT_UPLOADED,
  FAILED_VALIDATION: ConnectionFileStatus.ERRORED,
};

interface FileStatusProps {
  id: StrictString;
  archiveData: {
    archived: boolean;
  };
  scratchFile: {
    uploadStatus: string;
  };
}

export function getConnectionFileStatus(
  connectionFile: FileStatusProps,
  showArchivedStatus: boolean
): ConnectionFileStatus {
  const isArchived = connectionFile.archiveData?.archived || false;
  const uploadStatus =
    MAP_FILE_UPLOAD_STATUS_TO_CONNECTION_FILE_STATUS[connectionFile.scratchFile.uploadStatus];
  assert(
    uploadStatus != null,
    `Unknown upload status: ${connectionFile.scratchFile.uploadStatus} for file ${connectionFile.id}`
  );

  return isArchived && showArchivedStatus ? ConnectionFileStatus.ARCHIVED : uploadStatus;
}

export function fileStatusDisplay(connectionFile: FileStatusProps, showArchivedStatus: boolean): JSX.Element {
  const status = getConnectionFileStatus(connectionFile, showArchivedStatus);

  return (
    <>
      <BenchlingIcon
        className={css(styles.statusIcon, styles[FILE_STATUS_TO_STYLE[status]])}
        icon={FILE_STATUS_TO_ICON[status]}
      />
      {FILE_STATUS_TO_TEXT[status]}
    </>
  );
}
